import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, User, Shield, Building2, CreditCard, Wallet, KeyRound, Sun, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

function initials(name = "") {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "•";
}

export function ProfileMenu({ theme, onToggleTheme }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [tenant, setTenant] = useState(null);
  const nav = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (!boxRef.current?.contains(e.target) && !e.target.closest?.("[data-testid='v2-profile-btn']")) setOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Fetch wallet lazily on open
    api.get("/billing/wallet").then((r) => setWallet(r.data)).catch(() => {});
    if (user?.tenant_id) {
      api.get(`/tenants/${user.tenant_id}`).then((r) => setTenant(r.data)).catch(() => {});
    }
  }, [open, user?.tenant_id]);

  if (!user) return null;

  return (
    <div style={{ position: "relative" }}>
      <button
        className="v2-btn v2-btn-ghost"
        onClick={() => setOpen((v) => !v)}
        data-testid="v2-profile-btn"
        style={{ padding: "6px 10px 6px 6px", gap: 8 }}
      >
        <div style={{
          width: 26, height: 26, borderRadius: 999,
          background: "var(--v2-grad-brand)", color: "#fff",
          display: "grid", placeItems: "center",
          fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
        }}>{initials(user.name || user.email)}</div>
        <span style={{ fontWeight: 600, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {user.name || user.email?.split("@")[0]}
        </span>
        <ChevronDown size={13} strokeWidth={2} />
      </button>

      {open && (
        <div
          ref={boxRef}
          className="v2-pop"
          style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 320 }}
          data-testid="v2-profile-panel"
        >
          {/* Identity */}
          <div style={{ padding: 16, borderBottom: "1px solid var(--v2-border)", display: "flex", gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10,
              background: "var(--v2-grad-brand)", color: "#fff",
              display: "grid", placeItems: "center",
              fontSize: 15, fontWeight: 700,
            }}>{initials(user.name || user.email)}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>{user.name || "—"}</div>
              <div style={{ fontSize: 11, color: "var(--v2-text-muted)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis" }}>{user.email}</div>
              <span className="v2-chip v2-chip-primary" style={{ marginTop: 6 }}>{user.role?.replace(/_/g, " ")}</span>
            </div>
          </div>

          {/* Tenant summary */}
          {tenant && (
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--v2-border)", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--v2-surface-hover)", display: "grid", placeItems: "center" }}>
                <Building2 size={14} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tenant.name}</div>
                <div className="v2-label-mono" style={{ fontSize: 9.5 }}>{tenant.plan?.toUpperCase() || "PLAN"} · {tenant.country || "IN"}</div>
              </div>
            </div>
          )}

          {/* Wallet */}
          {wallet && (
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--v2-border)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Wallet size={13} color="var(--v2-primary-2)" />
                  <span className="v2-label-mono">Wallet</span>
                </div>
                <button className="v2-btn v2-btn-soft v2-btn-sm" onClick={() => { nav("/billing"); setOpen(false); }} data-testid="v2-profile-topup">
                  Top up
                </button>
              </div>
              <div style={{ fontFamily: "var(--v2-font-display)", fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                {(wallet.balance_credits || 0).toLocaleString("en-IN")} <span style={{ fontSize: 11, color: "var(--v2-text-muted)", fontWeight: 500 }}>credits</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--v2-text-muted)", marginTop: 2 }}>
                Lifetime {(wallet.lifetime_added || 0).toLocaleString("en-IN")} · Used {(wallet.lifetime_consumed || 0).toLocaleString("en-IN")}
              </div>
            </div>
          )}

          {/* Menu items */}
          <div style={{ padding: 6 }}>
            {[
              { icon: User,       label: "Profile & Security", to: "/profile", testid: "v2-profile-nav-profile" },
              { icon: KeyRound,   label: "API Keys",           to: "/developer", testid: "v2-profile-nav-dev" },
              { icon: CreditCard, label: "Billing & Invoices", to: "/billing", testid: "v2-profile-nav-billing" },
              { icon: Shield,     label: "Team & Roles",       to: "/users", testid: "v2-profile-nav-users" },
            ].map((it) => {
              const Icon = it.icon;
              return (
                <button
                  key={it.label}
                  onClick={() => { nav(it.to); setOpen(false); }}
                  data-testid={it.testid}
                  style={{
                    width: "100%", background: "transparent", border: 0,
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px", borderRadius: 6, cursor: "pointer",
                    fontSize: 13, color: "var(--v2-text-2)", textAlign: "left",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--v2-surface-hover)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <Icon size={14} strokeWidth={1.8} />
                  {it.label}
                </button>
              );
            })}

            <button
              onClick={() => { onToggleTheme?.(); }}
              data-testid="v2-profile-theme"
              style={{
                width: "100%", background: "transparent", border: 0,
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 10px", borderRadius: 6, cursor: "pointer",
                fontSize: 13, color: "var(--v2-text-2)", textAlign: "left",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--v2-surface-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </button>
          </div>

          <div style={{ borderTop: "1px solid var(--v2-border)", padding: 6 }}>
            <button
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="v2-profile-logout"
              style={{
                width: "100%", background: "transparent", border: 0,
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px", borderRadius: 6, cursor: "pointer",
                fontSize: 13, color: "var(--v2-danger)", textAlign: "left", fontWeight: 600,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--v2-danger-soft)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <LogOut size={14} strokeWidth={2} />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
